<template>
  <div class="acquisitions-page">
    <header class="page-heading">
      <div>
        <p>Acquisitions</p>
        <h1>Achats remportés</h1>
        <span>Suivez les coûts, le contenu et l’avancement de vos lots.</span>
      </div>
      <button type="button" class="primary-action" @click="createOpen = true">
        ＋ Créer un lot manuel
      </button>
    </header>
    <section class="summary" aria-label="Synthèse des achats">
      <div>
        <strong>{{ totalOffers }}</strong
        ><span>Achats au total</span>
      </div>
      <div>
        <strong>{{ loadedCost }}</strong
        ><span>Coût chargé</span>
      </div>
      <div>
        <strong>{{ formatMoney(totalRevenue) }}</strong
        ><span>Revenus chargés</span>
      </div>
      <p>Les montants correspondent aux achats actuellement chargés.</p>
    </section>
    <section class="acquisitions-panel">
      <nav class="tabs" aria-label="Filtrer les achats">
        <button
          type="button"
          :class="{ active: !filters.sold }"
          @click="setSoldFilter(false)"
        >
          Tous les achats</button
        ><button
          type="button"
          :class="{ active: filters.sold }"
          @click="setSoldFilter(true)"
        >
          Avec des ventes
        </button>
      </nav>
      <div class="toolbar">
        <label
          ><i class="fas fa-search" aria-hidden="true"></i
          ><input
            v-model="query"
            type="search"
            placeholder="Rechercher dans tous les achats…"
            @input="debouncedFilter" /></label
        ><select
          v-model="filters.source"
          aria-label="Filtrer par source"
          @change="filterOffers"
        >
          <option value="">Toutes les sources</option>
          <option v-for="(source, id) in SOURCES" :key="id" :value="id">
            {{ source.longLabel }}
          </option></select
        ><button
          v-if="hasActiveFilters"
          type="button"
          class="reset"
          @click="resetFilters"
        >
          Réinitialiser
        </button>
      </div>
      <div v-if="loading" class="loading">
        <LoaderDiv
          :isLoading="true"
          variant="modern"
          loaderText="Chargement des achats…"
        />
      </div>
      <div v-else-if="!visibleOffers.length" class="empty">
        <span>◎</span>
        <h2>Aucun achat trouvé</h2>
        <p>
          {{
            query
              ? "Modifiez votre recherche pour afficher davantage de résultats."
              : "Les annonces remportées apparaîtront ici."
          }}
        </p>
      </div>
      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Achat</th>
              <th>Coût</th>
              <th>Revenus</th>
              <th>Contenu</th>
              <th>Date</th>
              <th><span class="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="offer in visibleOffers" :key="offer._key">
              <td>
                <button
                  type="button"
                  class="identity"
                  @click="showDetails(offer)"
                >
                  <span class="thumb"
                    ><img
                      :src="getThumbnailUrl(offer._key)"
                      :alt="offer.title || 'Miniature'"
                      @error="handleImageError" /></span
                  ><span
                    ><strong>{{ offer.title || "Achat sans titre" }}</strong
                    ><small
                      >#{{ offer._key }} · {{ getSourceInfo(offer.source).label
                      }}<span class="compact-cost">
                        · {{ formatMoney(offer.cost) }}</span
                      ></small
                    ></span
                  >
                </button>
              </td>
              <td>
                <strong class="money">{{ formatMoney(offer.cost) }}</strong>
              </td>
              <td>
                <div class="revenue">
                  <strong>{{ formatMoney(offer.items?.price) }}</strong
                  ><small v-if="saleToCostPercentage(offer)"
                    >{{ saleToCostPercentage(offer) }} % du coût</small
                  >
                </div>
              </td>
              <td>
                <div class="content">
                  <strong
                    >{{ offer.items?.count || 0 }} objet{{
                      Number(offer.items?.count) > 1 ? "s" : ""
                    }}</strong
                  ><small
                    >{{ offer.items?.sold || 0 }} vendu{{
                      Number(offer.items?.sold) > 1 ? "s" : ""
                    }}</small
                  >
                </div>
              </td>
              <td>
                <time>{{
                  formatDate(offer.endtime || offer.purchase_date)
                }}</time>
                <button
                  v-if="offer.package_id"
                  type="button"
                  class="package-chip"
                  :title="`Ouvrir le colis ${offer.packageRef || ''}`"
                  @click="openPackage(offer.package_id)"
                >
                  Colis {{ offer.packageRef }}
                </button>
              </td>
              <td>
                <div class="actions">
                  <button
                    type="button"
                    class="refresh"
                    :disabled="refreshingId === offer._key"
                    :aria-label="
                      refreshingId === offer._key
                        ? 'Actualisation en cours'
                        : 'Actualiser cet achat'
                    "
                    @click="refreshOffer(offer)"
                  >
                    ↻
                    <span class="refresh-label">{{
                      refreshingId === offer._key
                        ? "Actualisation…"
                        : "Actualiser"
                    }}</span></button
                  ><button
                    type="button"
                    class="details"
                    @click="showDetails(offer)"
                  >
                    Détails →
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="loadingMore" class="load-more">Chargement…</div>
      <p v-if="allOffersLoaded && offers.length" class="end">
        {{ offers.length }} achat{{ offers.length > 1 ? "s" : "" }} chargé{{
          offers.length > 1 ? "s" : ""
        }}
      </p>
    </section>
    <AcquisitionQuickPanel
      :visible="panelOpen"
      :offer="selectedOffer"
      :offer-loading="offerLoading"
      :items="managedItems"
      :items-loading="itemsLoading"
      :items-loaded="itemsLoaded"
      :items-error="itemsError"
      @close="panelOpen = false"
      @refresh="refreshOffer"
      @edit="openEdit"
      @manage-items="openItemsManager"
      @tab-change="handlePanelTabChange"
      @open-item="
        (key) =>
          openDetail('item', key, {
            from: 'purchase',
            fromId: selectedOffer._key,
          })
      "
      @open-package="openPackage"
    />
    <OfferEditPopup
      :visible="editOpen"
      :offer="selectedOffer"
      :busy="savingOffer"
      :error="editError"
      @close="editOpen = false"
      @save="saveOffer"
    />
    <OfferCreatePopup
      :is-visible="createOpen"
      @close="createOpen = false"
      @created="handleManualCreated"
    />
  </div>
</template>

<script>
import { formatDate as formatDateText } from "../../clock";
import {
  computed,
  nextTick,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  ref,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import config from "@/config/config.js";
import { isTokenExpired } from "../../auth";
import { parse } from "@/common.js";
import LoaderDiv from "../LoaderDiv.vue";
import { dropQueryParams } from "../../urlQuery";
import { navigateFromDetail, openDetail } from "../../detailStack";
import AcquisitionQuickPanel from "./AcquisitionQuickPanel.vue";
import OfferEditPopup from "./OfferEditPopup.vue";
import OfferCreatePopup from "./OfferCreatePopup.vue";
import { SOURCES, sourceInfo } from "../../sources";
export default {
  name: "MyOffersList",
  components: {
    AcquisitionQuickPanel,
    LoaderDiv,
    OfferCreatePopup,
    OfferEditPopup,
  },
  setup() {
    let itemsRequestId = 0,
      offerRequestId = 0;
    const router = useRouter(),
      route = useRoute(),
      offers = ref([]),
      loading = ref(true),
      loadingMore = ref(false),
      allOffersLoaded = ref(false),
      currentPage = ref(0),
      totalOffers = ref(0),
      query = ref(""),
      selectedOffer = ref({}),
      panelOpen = ref(false),
      editOpen = ref(false),
      savingOffer = ref(false),
      editError = ref(""),
      createOpen = ref(false),
      managedItems = ref([]),
      itemsLoading = ref(false),
      itemsLoaded = ref(false),
      itemsError = ref(""),
      offerLoading = ref(false),
      refreshingId = ref(null),
      filters = ref({ sold: false, source: "" }),
      listScrollY = ref(0);
    const limit = config.itemsLimit,
      cdnUrl = config.cdnUrl;
    const visibleOffers = computed(() => offers.value);
    const hasActiveFilters = computed(() =>
      Boolean(query.value.trim() || filters.value.sold || filters.value.source),
    );
    const totalRevenue = computed(() =>
      offers.value
        .reduce((sum, o) => sum + (Number(o.items?.price) || 0), 0)
        .toFixed(2),
    );
    const loadedCost = computed(() =>
      new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(
        offers.value.reduce((sum, o) => sum + (Number(o.cost) || 0), 0),
      ),
    );
    const getThumbnailUrl = (id) =>
      `${cdnUrl}/images/${id}/${id}_thumbnail.jpg`;
    const getSourceInfo = sourceInfo;
    const formatMoney = (value) =>
      new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(Number(value) || 0);
    const formatDate = (value) => formatDateText(value, undefined, "—");
    const saleToCostPercentage = (offer) => {
      const cost = Number(offer.cost),
        revenue = Number(offer.items?.price);
      return cost && revenue ? ((revenue / cost) * 100).toFixed(0) : null;
    };
    const handleImageError = (event) => {
      event.target.style.visibility = "hidden";
    };
    const checkAuth = () => {
      const token = localStorage.getItem("access_token");
      if (isTokenExpired(token)) {
        router.push("/");
        return false;
      }
      return true;
    };
    // Only the answer to the latest request counts: a search typed while a
    // page loads, a scroll during the first load (no duplicate, no page
    // skipped, no results of the previous search)
    let listRequest = 0,
      listBusy = false;
    const loadOffers = async () => {
      if (allOffersLoaded.value || listBusy || !checkAuth()) return;
      listBusy = true;
      const request = ++listRequest;
      currentPage.value ? (loadingMore.value = true) : (loading.value = true);
      try {
        const params = new URLSearchParams({ page: currentPage.value, limit });
        if (query.value.trim()) params.set("contains", query.value.trim());
        if (filters.value.sold) params.set("sold", "true");
        if (filters.value.source) params.set("source", filters.value.source);
        const response = await axios.get(
          `${config.apiUrl}/offers/owned?${params}`,
        );
        if (request !== listRequest) return;
        const results = response.data?.result || [];
        offers.value.push(...results);
        totalOffers.value = response.data?.total_items || 0;
        currentPage.value += 1;
        allOffersLoaded.value = offers.value.length >= totalOffers.value;
      } catch (error) {
        if (request === listRequest)
          console.error("Impossible de charger les achats", error);
      } finally {
        if (request === listRequest) {
          loading.value = false;
          loadingMore.value = false;
          listBusy = false;
        }
      }
    };
    // Back on the (cached) page: reloads what was already shown, without
    // emptying the list nor losing the scroll position
    const refreshOffers = async () => {
      if (listBusy || !checkAuth()) return;
      const request = ++listRequest;
      listBusy = true;
      try {
        const params = new URLSearchParams({
          page: 0,
          limit: Math.max(offers.value.length, limit),
        });
        if (query.value.trim()) params.set("contains", query.value.trim());
        if (filters.value.sold) params.set("sold", "true");
        if (filters.value.source) params.set("source", filters.value.source);
        const response = await axios.get(
          `${config.apiUrl}/offers/owned?${params}`,
        );
        if (request !== listRequest) return;
        const results = response.data?.result || [];
        offers.value = results;
        totalOffers.value = response.data?.total_items || 0;
        currentPage.value = Math.ceil(results.length / limit);
        allOffersLoaded.value = offers.value.length >= totalOffers.value;
      } catch (error) {
        console.error("Impossible d’actualiser les achats", error);
      } finally {
        if (request === listRequest) listBusy = false;
      }
    };
    let searchTimer = null;
    const filterOffers = async () => {
      listRequest++; // the request in progress is for the previous search
      listBusy = false;
      offers.value = [];
      currentPage.value = 0;
      allOffersLoaded.value = false;
      await loadOffers();
    };
    const debouncedFilter = () => {
      if (searchTimer) clearTimeout(searchTimer);
      searchTimer = setTimeout(filterOffers, 300);
    };
    const setSoldFilter = (value) => {
      if (filters.value.sold === value) return;
      filters.value.sold = value;
      filterOffers();
    };
    const resetFilters = () => {
      if (searchTimer) clearTimeout(searchTimer);
      query.value = "";
      filters.value = { sold: false, source: "" };
      filterOffers();
    };
    const loadItems = async (offer) => {
      const requestId = ++itemsRequestId;
      itemsLoading.value = true;
      itemsError.value = "";
      managedItems.value = [];
      try {
        const response = await axios.get(
          `${config.apiUrl}/offers/${offer._key}/items`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          },
        );
        if (requestId === itemsRequestId) {
          const payload = response.data;
          managedItems.value = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.result)
              ? payload.result
              : Array.isArray(payload?.items)
                ? payload.items
                : [];
          itemsLoaded.value = true;
        }
      } catch (error) {
        console.error("Impossible de charger les objets de cet achat", error);
        if (requestId === itemsRequestId)
          itemsError.value =
            "Le contenu détaillé est momentanément indisponible.";
      } finally {
        if (requestId === itemsRequestId) itemsLoading.value = false;
      }
    };
    const loadOfferDetails = async (offer) => {
      const requestId = ++offerRequestId;
      offerLoading.value = true;
      try {
        const response = await axios.get(
          `${config.apiUrl}/offers/${offer._key}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          },
        );
        if (requestId === offerRequestId) {
          selectedOffer.value = { ...offer, ...response.data };
          detailsLoadedFor = offer._key;
        }
      } catch (error) {
        console.error(
          "Impossible de charger les informations de cet achat",
          error,
        );
      } finally {
        if (requestId === offerRequestId) offerLoading.value = false;
      }
    };
    const showDetails = (offer) => {
      selectedOffer.value = offer;
      managedItems.value = [];
      itemsError.value = "";
      itemsLoaded.value = false;
      panelOpen.value = true;
      loadOfferDetails(offer);
    };
    const handlePanelTabChange = (tab) => {
      if (tab === "content" && !itemsLoaded.value && !itemsLoading.value)
        loadItems(selectedOffer.value);
    };
    const navigateToItems = (offerId) =>
      router.push({ path: "/items", query: { offerId } });
    const refreshOffer = async (offer) => {
      refreshingId.value = offer._key;
      try {
        await parse(offer);
        // The row and the open sheet updated in place (scroll kept)
        await refreshOffers();
        if (panelOpen.value && selectedOffer.value?._key === offer._key)
          await loadOfferDetails(offer);
      } catch (error) {
        console.error("Impossible d’actualiser cet achat", error);
      } finally {
        refreshingId.value = null;
      }
    };
    // The form shows the costs of the purchase: never from a list row (which
    // has only the total), else saving would replace them by zeros
    let detailsLoadedFor = null;
    const openEdit = async (offer) => {
      if (detailsLoadedFor !== offer._key) {
        await loadOfferDetails(offer);
        if (detailsLoadedFor !== offer._key) {
          window.alert(
            "Les informations de cet achat n’ont pas pu être chargées. Réessayez dans un instant.",
          );
          return;
        }
      } else selectedOffer.value = offer;
      panelOpen.value = false;
      editError.value = "";
      editOpen.value = true;
    };
    const openItemsManager = (offer) => {
      selectedOffer.value = offer;
      panelOpen.value = false;
      listScrollY.value = window.scrollY;
      router.push({
        name: "acquisition-items",
        params: { offerId: offer._key },
      });
    };
    const saveOffer = async (data) => {
      const {
        reference = null,
        packageRef = null,
        title = "",
        description = "",
        url = "",
        source = "",
        ...costs
      } = data;
      const transactionCosts = { ...costs };
      transactionCosts.totalCost = Object.entries(costs).reduce(
        (sum, [key, value]) =>
          key === "totalCost" ? sum : sum + (Number(value) || 0),
        0,
      );
      if (savingOffer.value) return;
      savingOffer.value = true;
      editError.value = "";
      try {
        await axios.post(`${config.apiUrl}/offers/${selectedOffer.value._key}`, {
          transactionCosts,
          reference,
          packageRef,
          offer: { title, description, url, source },
        });
      } catch (error) {
        console.error("Enregistrement de l’achat impossible", error);
        editError.value =
          "L’achat n’a pas pu être enregistré. Vos saisies restent affichées : réessayez dans un instant.";
        return;
      } finally {
        savingOffer.value = false;
      }
      editOpen.value = false;
      detailsLoadedFor = null;
      await refreshOffers();
    };
    const handleManualCreated = async (created) => {
      createOpen.value = false;
      await filterOffers();
      const payload = created?.result || created;
      const key = payload?._key || payload?.offer_id;
      if (key) {
        const offer =
          offers.value.find((entry) => entry._key === key) || payload;
        showDetails(offer);
      }
    };
    const openRequestedOffer = async () => {
      const key = String(route.query.offerId || "");
      if (!key) return;
      let offer = offers.value.find((entry) => entry._key === key);
      if (!offer) {
        try {
          const response = await axios.get(`${config.apiUrl}/offers/${key}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          });
          offer = response.data?.result || response.data;
        } catch (error) {
          console.error("Impossible d’ouvrir l’achat demandé", error);
        }
      }
      // "&edit=1": straight to the purchase form (link from a sheet)
      const edit = route.query.edit === "1";
      dropQueryParams("offerId", "edit");
      if (!offer) return;
      if (edit) {
        selectedOffer.value = offer;
        await loadOfferDetails(offer);
        openEdit(selectedOffer.value);
      } else showDetails(offer);
    };
    const onScroll = () => {
      listScrollY.value = window.scrollY;
      if (
        window.scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 500
      )
        loadOffers();
    };
    const activateList = async () => {
      window.addEventListener("scroll", onScroll, { passive: true });
      await nextTick();
      window.scrollTo({ top: listScrollY.value });
    };
    const deactivateList = () => window.removeEventListener("scroll", onScroll);
    let firstActivation = true;
    onMounted(loadOffers);
    // Also on reactivation: this instance stays cached for its URL
    onActivated(() => {
      activateList();
      openRequestedOffer();
      if (!firstActivation) refreshOffers();
      firstActivation = false;
    });
    onDeactivated(deactivateList);
    // A purchase changed in a sheet opened elsewhere (won, removed from the
    // purchases): the loaded rows are read again, scroll and filters kept
    const onChanged = (event) => {
      if (event.detail?.type === "offer" && "owns" in (event.detail.record || {}))
        refreshOffers();
    };
    window.addEventListener("prexens:changed", onChanged);
    onUnmounted(() => window.removeEventListener("prexens:changed", onChanged));
    onUnmounted(() => {
      if (searchTimer) clearTimeout(searchTimer);
    });
    // The package of a purchase: the editor page (this list keeps its state)
    const openPackage = (packageId) =>
      navigateFromDetail(`/packages/${packageId}`);
    return {
      openDetail,
      openPackage,
      allOffersLoaded,
      createOpen,
      debouncedFilter,
      editOpen,
      editError,
      savingOffer,
      filterOffers,
      filters,
      formatDate,
      formatMoney,
      getSourceInfo,
      SOURCES,
      getThumbnailUrl,
      handleImageError,
      handleManualCreated,
      handlePanelTabChange,
      hasActiveFilters,
      itemsError,
      itemsLoaded,
      itemsLoading,
      loadedCost,
      loading,
      loadingMore,
      managedItems,
      navigateToItems,
      offerLoading,
      offers,
      openEdit,
      openItemsManager,
      panelOpen,
      query,
      refreshingId,
      refreshOffer,
      resetFilters,
      saleToCostPercentage,
      saveOffer,
      selectedOffer,
      setSoldFilter,
      showDetails,
      totalOffers,
      totalRevenue,
      visibleOffers,
    };
  },
};
</script>

<style scoped>
.acquisitions-page {
  --text: #202b3c;
  --muted: #6f7b8d;
  --line: #e3e8ef;
  --green: #168a64;
  min-height: 100%;
  padding: var(--page-gutter);
  background: #f5f7fa;
  color: var(--text);
}
.page-heading,
.summary,
.acquisitions-panel {
  max-width: var(--content-width);
  margin-inline: auto;
}
.page-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
}
.page-heading p {
  margin: 0 0 8px;
  color: var(--green);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.page-heading h1 {
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: var(--page-title-size);
  letter-spacing: -0.035em;
}
.page-heading span {
  display: block;
  margin-top: 8px;
  color: var(--muted);
  font-size: 14px;
}
.primary-action {
  height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  background: #168a64;
  color: #fff;
  font-size: 13px;
  font-weight: 750;
  white-space: nowrap;
}
.summary {
  display: flex;
  margin-bottom: 16px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
}
.summary div {
  display: flex;
  min-width: 180px;
  flex-direction: column;
  padding: 16px 20px;
  border-right: 1px solid var(--line);
}
.summary strong {
  font-size: 20px;
}
.summary span {
  margin-top: 4px;
  color: var(--muted);
  font-size: 12px;
}
.summary p {
  align-self: center;
  margin: 0 20px 0 auto;
  color: #87919f;
  font-size: 11px;
}
.acquisitions-panel {
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
}
.tabs {
  display: flex;
  gap: 24px;
  height: 53px;
  padding: 0 22px;
  border-bottom: 1px solid var(--line);
}
.tabs button {
  position: relative;
  border: 0;
  background: none;
  color: #778294;
  font-size: 13px;
  font-weight: 700;
}
.tabs button.active {
  color: var(--text);
}
.tabs button.active:after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  background: var(--green);
  content: "";
}
.toolbar {
  display: flex;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--line);
  background: #fbfcfd;
}
.toolbar label {
  display: flex;
  width: min(420px, 100%);
  height: 38px;
  align-items: center;
  gap: 9px;
  padding: 0 12px;
  border: 1px solid #d5dce5;
  border-radius: 8px;
  background: #fff;
  color: #8993a1;
}
.toolbar input {
  width: 100%;
  border: 0;
  outline: 0;
}
.toolbar select {
  height: 38px;
  padding: 0 34px 0 12px;
  border: 1px solid #d5dce5;
  border-radius: 8px;
  background: #fff;
  color: #4e5a6c;
}
.reset {
  height: 38px;
  padding: 0 12px;
  border: 0;
  background: transparent;
  color: #667285;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.table-wrap {
  overflow-x: auto;
}
.acquisitions-panel table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}
.acquisitions-panel table th:first-child {
  width: auto;
}
.acquisitions-panel table th:nth-child(2) {
  width: 120px;
}
.acquisitions-panel table th:nth-child(3) {
  width: 130px;
}
.acquisitions-panel table th:nth-child(4) {
  width: 135px;
}
.acquisitions-panel table th:nth-child(5) {
  width: 110px;
}
.acquisitions-panel table th:last-child {
  width: 205px;
}
.acquisitions-panel table th {
  padding: 11px 16px;
  border-bottom: 1px solid var(--line);
  background: #fff !important;
  color: #7d8796 !important;
  font-size: 11px;
  letter-spacing: 0.05em;
  text-align: left;
  text-transform: uppercase;
}
.acquisitions-panel table td {
  min-width: 0;
  padding: 13px 16px;
  border-bottom: 1px solid #edf0f4;
  vertical-align: middle;
}
.acquisitions-panel table tbody tr:hover {
  background: #fafcfd;
}
.identity {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 12px;
  border: 0;
  background: none;
  text-align: left;
}
.thumb {
  display: grid;
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  overflow: hidden;
  border: 1px solid #e1e6ec;
  border-radius: 7px;
  background: #f1f4f7;
}
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.identity > span:last-child {
  min-width: 0;
  flex: 1;
}
.identity strong,
.identity small {
  display: block;
}
.identity strong {
  width: 100%;
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.identity small {
  overflow: hidden;
  margin-top: 5px;
  color: #8993a1;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.compact-cost {
  display: none;
}
.money,
.revenue strong,
.content strong {
  font-size: 13px;
}
.revenue,
.content {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}
.revenue small,
.content small,
time {
  color: #84909f;
  font-size: 11px;
}
.package-chip {
  display: block;
  margin-top: 4px;
  padding: 0;
  border: 0;
  background: none;
  color: #2e6098;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}
.package-chip:hover {
  text-decoration: underline;
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.actions button {
  height: 34px;
  border: 1px solid #d8dee6;
  border-radius: 7px;
  background: #fff;
  color: #596679;
  font-size: 12px;
  font-weight: 700;
}
.refresh {
  min-width: 92px;
}
.refresh:disabled {
  opacity: 0.45;
}
.details {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 11px;
  white-space: nowrap;
}
.loading,
.empty {
  min-height: 340px;
}
.empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
}
.empty > span {
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  border-radius: 12px;
  background: #eaf7f2;
  color: var(--green);
}
.empty h2 {
  margin: 16px 0 7px;
  font-size: 17px;
}
.empty p {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
}
.load-more,
.end {
  margin: 0;
  padding: 16px;
  text-align: center;
  color: #87919f;
  font-size: 12px;
}
.end {
  border-top: 1px solid #edf0f4;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
@media (max-width: 1400px) {
  .acquisitions-panel table th:nth-child(3),
  .acquisitions-panel table td:nth-child(3) {
    display: none;
  }
  .acquisitions-panel table th:nth-child(2) {
    width: 105px;
  }
  .acquisitions-panel table th:nth-child(4) {
    width: 115px;
  }
  .acquisitions-panel table th:nth-child(5) {
    width: 90px;
  }
  .acquisitions-panel table th:last-child {
    width: 150px;
  }
  .compact-cost {
    display: inline;
  }
  .refresh-label {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }
  .refresh {
    width: 34px;
    min-width: 34px;
  }
  .actions button {
    font-size: 11px;
  }
  .acquisitions-panel table th,
  .acquisitions-panel table td {
    padding-inline: 12px;
  }
  .summary p {
    display: none;
  }
  .summary div {
    min-width: 0;
    flex: 1;
  }
}
@media (max-width: 800px) {
  .acquisitions-page {
    padding: 20px 14px;
  }
  .summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .summary div {
    padding: 13px 14px;
  }
  .summary div:last-of-type {
    border-right: 0;
  }
  .tabs {
    gap: 16px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
  }
  .tabs::-webkit-scrollbar {
    display: none;
  }
  .toolbar {
    flex-direction: column;
  }
  .toolbar label {
    width: 100%;
  }
  .page-heading h1 {
    font-size: 26px;
  }
  .acquisitions-panel table th:nth-child(4),
  .acquisitions-panel table td:nth-child(4) {
    display: none;
  }
}
/* Phone: title (with its cost below) and actions only */
@media (max-width: 560px) {
  .page-heading {
    align-items: stretch;
    flex-direction: column;
  }
  .primary-action {
    width: 100%;
  }
  .acquisitions-panel table th:nth-child(2),
  .acquisitions-panel table td:nth-child(2),
  .acquisitions-panel table th:nth-child(5),
  .acquisitions-panel table td:nth-child(5) {
    display: none;
  }
  .acquisitions-panel table th:last-child {
    width: 118px;
  }
  .acquisitions-panel table th,
  .acquisitions-panel table td {
    padding-inline: 10px;
  }
  .tabs button {
    white-space: nowrap;
  }
}
</style>
